import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { MdEdit, MdRefresh, MdSearchOff } from "react-icons/md";
import Badge from "components/badge/Badge";
import Button from "components/button";
import Card from "components/card";
import { PageContainer, PageHeader } from "components/page";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import Tabs, { TabPanel } from "components/tabs";
import CampaignStatusBadge from "components/heartbeat/CampaignStatusBadge";
import { isTextQuestion } from "components/heartbeat/analytics";
import heartbeatCampaignService from "services/heartbeatCampaignService";
import heartbeatResponseService from "services/heartbeatResponseService";
import notify from "lib/notify";
import { getApiErrorMessage } from "api";
import { formatDateTime } from "utils/format";
import { track } from "lib/mixpanel";
import { EVENTS } from "lib/events";
import OverviewTab from "./campaign-details/OverviewTab";
import ResponsesTab from "./campaign-details/ResponsesTab";
import QuestionsTab from "./campaign-details/QuestionsTab";
import QrTab from "./campaign-details/QrTab";
import ResponseDialog from "./campaign-details/ResponseDialog";

const TABS_ID = "campaign-tabs";
const BACK = { backTo: "/admin/heartbeat", backLabel: "Back to Campaigns" };

const CampaignDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const responseIdParam = searchParams.get("responseId");

  const [loading, setLoading] = useState(true);
  const [campaignError, setCampaignError] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [responses, setResponses] = useState([]);
  const [responsesLoading, setResponsesLoading] = useState(true);
  const [responsesError, setResponsesError] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState(() => searchParams.get("tab") || "overview");
  const [selectedResponse, setSelectedResponse] = useState(null);

  const [qrStats, setQrStats] = useState(null);
  const [qrSize, setQrSize] = useState(256);
  const [generatingQr, setGeneratingQr] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [addingPersonId, setAddingPersonId] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiAnalysisExpanded, setAiAnalysisExpanded] = useState(true);

  const [questionAnalytics, setQuestionAnalytics] = useState(null);
  const [loadingQuestionAnalytics, setLoadingQuestionAnalytics] = useState(false);
  const [generatingQuestionSummary, setGeneratingQuestionSummary] = useState({});

  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [textResponses, setTextResponses] = useState([]);
  const [loadingTextResponses, setLoadingTextResponses] = useState(false);
  const [textSearchQuery, setTextSearchQuery] = useState("");
  const [textSortOrder, setTextSortOrder] = useState("newest");

  useEffect(() => {
    if (!id) {
      notify.error("Campaign ID is required");
      navigate("/admin/heartbeat");
      return;
    }
    fetchCampaign();
    fetchResponses();
    fetchStats();
    fetchAnalytics();
    fetchQrStats();
    fetchQuestionAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Open the response dialog when navigated with ?responseId=
  useEffect(() => {
    if (responseIdParam && responses.length > 0) {
      const match = responses.find((r) => r.id === parseInt(responseIdParam));
      if (match) setSelectedResponse(match);
    }
  }, [responseIdParam, responses]);

  useEffect(() => {
    if (campaign?.id) {
      track(EVENTS.HEARTBEAT_CAMPAIGN_VIEWED, {
        campaign_id: campaign.id,
        campaign_name: campaign.name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.id]);

  const fetchCampaign = async () => {
    try {
      setCampaignError(null);
      const response = await heartbeatCampaignService.getCampaignById(id);
      if (response.success) {
        setCampaign(response.data);
        if (response.data.aiSummary) {
          setAiSummary(response.data.aiSummary);
        }
      }
    } catch (error) {
      console.error("Error fetching campaign:", error);
      setCampaignError(error);
      if (campaign) notify.error(getApiErrorMessage(error, "Could not refresh campaign"));
    } finally {
      setLoading(false);
    }
  };

  const fetchResponses = async () => {
    try {
      setResponsesError(null);
      const response = await heartbeatResponseService.getResponsesByCampaign(id);
      if (response.success) {
        setResponses(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching responses:", error);
      setResponsesError(error);
    } finally {
      setResponsesLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await heartbeatCampaignService.getCampaignStats(id);
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await heartbeatCampaignService.getCampaignAnalytics(id);
      if (response.success) {
        setAnalytics(response.data);
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
    }
  };

  const fetchQrStats = async () => {
    try {
      const response = await heartbeatCampaignService.getQrStats(id);
      if (response.success) {
        setQrStats(response.data);
      }
    } catch (error) {
      console.error("Error fetching QR stats:", error);
    }
  };

  const fetchQuestionAnalytics = async () => {
    try {
      setLoadingQuestionAnalytics(true);
      const response = await heartbeatCampaignService.getQuestionAnalytics(id);
      if (response.success) {
        setQuestionAnalytics(response.data);
        if (!selectedQuestionId && response.data?.questions?.length > 0) {
          handleQuestionSelect(response.data.questions[0].questionId);
        }
      }
    } catch (error) {
      console.error("Error fetching question analytics:", error);
    } finally {
      setLoadingQuestionAnalytics(false);
    }
  };

  const refreshAll = () => {
    fetchCampaign();
    fetchResponses();
    fetchStats();
    fetchAnalytics();
    fetchQrStats();
    fetchQuestionAnalytics();
  };

  const handleGenerateQuestionSummary = async (questionId) => {
    setGeneratingQuestionSummary((prev) => ({ ...prev, [questionId]: true }));
    try {
      const response = await heartbeatCampaignService.generateQuestionSummary(id, questionId);
      if (response.success) {
        setQuestionAnalytics((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            questions: prev.questions.map((q) =>
              q.questionId === questionId
                ? { ...q, stats: { ...q.stats, aiSummary: response.data } }
                : q
            ),
          };
        });
        notify.success("AI insights generated");
      }
    } catch (error) {
      console.error("Error generating question summary:", error);
      notify.error(getApiErrorMessage(error, "Could not generate AI insights"));
    } finally {
      setGeneratingQuestionSummary((prev) => ({ ...prev, [questionId]: false }));
    }
  };

  const handleQuestionSelect = async (questionId) => {
    setSelectedQuestionId(questionId);
    const question = questionAnalytics?.questions?.find((q) => q.questionId === questionId);

    // Only text questions have a response list to fetch.
    if (question && isTextQuestion(question.type)) {
      setLoadingTextResponses(true);
      setTextResponses([]);
      try {
        const response = await heartbeatCampaignService.getQuestionResponses(id, questionId, {
          search: textSearchQuery,
          sort: textSortOrder,
        });
        if (response.success) {
          setTextResponses(response.data.responses || []);
        }
      } catch (error) {
        // The list stays empty; the rest of the question analytics still render.
        console.error("Error fetching question responses:", error);
      } finally {
        setLoadingTextResponses(false);
      }
    } else {
      setTextResponses([]);
    }
  };

  // SearchInput debounces, so this refetches once typing settles or the sort changes.
  useEffect(() => {
    if (selectedQuestionId) {
      const question = questionAnalytics?.questions?.find(
        (q) => q.questionId === selectedQuestionId
      );
      if (question && isTextQuestion(question.type)) {
        handleQuestionSelect(selectedQuestionId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textSearchQuery, textSortOrder]);

  const handleGenerateQrCode = async (regenerate = false) => {
    setGeneratingQr(true);
    try {
      const response = await heartbeatCampaignService.generateQrCode(id, regenerate);
      if (response.success) {
        setCampaign((prev) => ({
          ...prev,
          qrCodeToken: response.data.qrCodeToken,
          qrCodeUrl: response.data.qrCodeUrl,
          qrCodeShortUrl: response.data.qrCodeShortUrl,
        }));
        notify.success(regenerate ? "QR code regenerated" : "QR code generated");
        fetchQrStats();
      }
    } catch (error) {
      console.error("Error generating QR code:", error);
      notify.error(getApiErrorMessage(error, "Could not generate QR code"));
    } finally {
      setGeneratingQr(false);
    }
  };

  const handleGenerateSummary = async () => {
    setGeneratingSummary(true);
    try {
      const response = await heartbeatCampaignService.generateCampaignSummary(id);
      if (response.success && response.data) {
        setAiSummary(response.data);
        setCampaign((prev) => ({
          ...prev,
          aiSummary: response.data,
          lastSummaryGeneratedAt: response.data.generatedAt,
        }));
        notify.success("AI analysis generated");
      } else {
        notify.error(response.message || "Could not generate analysis");
      }
    } catch (error) {
      console.error("Error generating summary:", error);
      notify.error(getApiErrorMessage(error, "Could not generate AI analysis"));
    } finally {
      setGeneratingSummary(false);
    }
  };

  const handleAddPerson = async (responseId) => {
    setAddingPersonId(responseId);
    try {
      const result = await heartbeatResponseService.createPersonFromResponse(responseId);
      if (result.success) {
        notify.success(result.message || "Person added");
        fetchResponses();
      }
    } catch (error) {
      console.error("Error adding person:", error);
      notify.error(getApiErrorMessage(error, "Could not add person"));
    } finally {
      setAddingPersonId(null);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "responses") {
      track(EVENTS.HEARTBEAT_RESPONSES_VIEWED, { campaign_id: id });
    }
  };

  const editCampaign = () => navigate(`/admin/heartbeat/${id}/edit`);

  if (loading) {
    return (
      <PageContainer>
        <PageHeader title="Campaign Details" {...BACK} />
        <div className="flex flex-col gap-5" aria-busy="true">
          <Skeleton className="h-10 w-full max-w-md" />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
            {[0, 1, 2, 3].map((key) => (
              <Skeleton key={key} className="h-28 w-full rounded-card" />
            ))}
          </div>
          <Skeleton className="h-96 w-full rounded-card" />
        </div>
      </PageContainer>
    );
  }

  if (!campaign) {
    return (
      <PageContainer>
        <PageHeader title="Campaign Details" {...BACK} />
        <Card padding="md">
          {campaignError ? (
            <ErrorState
              message={getApiErrorMessage(campaignError, "The campaign could not be loaded.")}
              onRetry={() => {
                setLoading(true);
                fetchCampaign();
              }}
            />
          ) : (
            <EmptyState
              icon={MdSearchOff}
              title="Campaign Not Found"
              description="This campaign may have been deleted or you may not have access to it."
              action={
                <Button variant="secondary" onClick={() => navigate("/admin/heartbeat")}>
                  Back to Campaigns
                </Button>
              }
            />
          )}
        </Card>
      </PageContainer>
    );
  }

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "responses", label: "Responses", count: responses.length },
    { id: "questions", label: "Questions" },
    { id: "qrcode", label: "QR Code" },
  ];

  const renderTab = () => {
    switch (activeTab) {
      case "responses":
        return (
          <ResponsesTab
            responses={responses}
            loading={responsesLoading}
            error={responsesError}
            onRetry={fetchResponses}
            addingPersonId={addingPersonId}
            onAddPerson={handleAddPerson}
            onView={setSelectedResponse}
          />
        );
      case "questions":
        return <QuestionsTab questions={campaign.questions || []} />;
      case "qrcode":
        return (
          <QrTab
            campaign={campaign}
            qrStats={qrStats}
            qrSize={qrSize}
            onQrSizeChange={setQrSize}
            generatingQr={generatingQr}
            onGenerateQrCode={handleGenerateQrCode}
          />
        );
      default:
        return (
          <OverviewTab
            campaign={campaign}
            stats={stats}
            statsLoading={statsLoading}
            responsesCount={responses.length}
            analytics={analytics}
            qrStats={qrStats}
            onConfigure={editCampaign}
            questionAnalyticsProps={{
              questionAnalytics,
              loading: loadingQuestionAnalytics,
              selectedQuestionId,
              onSelectQuestion: handleQuestionSelect,
              textResponses,
              loadingTextResponses,
              searchQuery: textSearchQuery,
              onSearchChange: setTextSearchQuery,
              sortOrder: textSortOrder,
              onSortChange: setTextSortOrder,
              onGenerateQuestionSummary: handleGenerateQuestionSummary,
              generatingQuestionSummary,
            }}
            surveyAnalysisProps={{
              aiSummary,
              responseCount: responses.length,
              generating: generatingSummary,
              onGenerate: handleGenerateSummary,
              expanded: aiAnalysisExpanded,
              onToggleExpanded: () => setAiAnalysisExpanded((prev) => !prev),
            }}
          />
        );
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title={campaign.name}
        description={`Created ${formatDateTime(campaign.createdAt)}`}
        meta={
          <>
            <CampaignStatusBadge status={campaign.status} />
            {campaign.isSystem && <Badge tone="info">System</Badge>}
          </>
        }
        {...BACK}
        actions={
          <>
            <Button
              variant="secondary"
              leftIcon={<MdRefresh aria-hidden="true" />}
              onClick={() => {
                refreshAll();
                notify.success("Data refreshed");
              }}
            >
              Refresh
            </Button>
            <Button leftIcon={<MdEdit aria-hidden="true" />} onClick={editCampaign}>
              Edit Campaign
            </Button>
          </>
        }
        tabs={
          <Tabs
            id={TABS_ID}
            aria-label="Campaign sections"
            syncParam="tab"
            tabs={tabs}
            value={activeTab}
            onChange={handleTabChange}
          />
        }
      />

      <TabPanel tabsId={TABS_ID} tabId={activeTab}>
        {renderTab()}
      </TabPanel>

      <ResponseDialog
        response={selectedResponse}
        questions={campaign.questions || []}
        addingPersonId={addingPersonId}
        onAddPerson={handleAddPerson}
        onClose={() => setSelectedResponse(null)}
      />
    </PageContainer>
  );
};

export default CampaignDetails;
